import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button, IButtonProps } from '@/common/components/Button/Button';
import { IllustrationImage } from '@/common/components/Illustrations/IllustrationImage';
import { ModalLearning } from '@/common/components/ModalLearning/ModalLearning';
import { TModalLearningStepProps } from '@/common/components/ModalLearning/ModalLearningStep/ModalLearningStep';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useRouter } from 'expo-router';
import {
  TSinglePaymentModalType,
  updateModalLastView,
  useShouldShowModal,
} from './ITSinglePaymentModal.utils';

export function ITSinglePaymentModal() {
  const { shouldShow, modalType, merchantName } = useShouldShowModal();
  const [dismissed, setDismissed] = useState(false);

  const onClose = () => {
    setDismissed(true);
    updateModalLastView();
  };

  const stepProps = useModalStepProps(modalType, {
    onClose,
    merchantName,
  });

  if (!shouldShow || dismissed || !stepProps) {
    return null;
  }

  return (
    <ModalLearning.Root onClose={onClose}>
      <ModalLearning.Step
        {...stepProps}
        asset={
          <IllustrationImage
            source={require('@/assets/illustrations/illustration-single-payment-campaign.png')}
            transition={250}
          />
        }
      />
    </ModalLearning.Root>
  );
}

function useModalStepProps(
  modalType: TSinglePaymentModalType | null,
  {
    onClose,
    merchantName,
  }: { onClose: () => void; merchantName: string | null | undefined }
): TModalLearningStepProps | null {
  const { t } = useTranslation();
  const router = useRouter();

  switch (modalType) {
    case 'failed_payment_no_strategy':
      return {
        title: t(
          'dashboard.home.it.single_payment_modal.failed_payment_no_strategy.title'
        ),
        description: (
          <ModalStepDescription
            i18nKey={t(
              'dashboard.home.it.single_payment_modal.failed_payment_no_strategy.description',
              { merchant_name: merchantName }
            )}
            primaryAction={{
              children: t(
                'dashboard.home.it.single_payment_modal.select_payment_method'
              ),
              onPress: () => {
                AnalyticsService.trackCustomEvent(
                  'single_transaction_launching_strategy_learning_center_select_payment_button_clicked'
                );
                onClose();
                router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT);
              },
            }}
            secondaryAction={{
              children: t('dashboard.home.it.single_payment_modal.not_now'),
              onPress: () => {
                onClose();
              },
            }}
          />
        ),
        onShow: () => {
          AnalyticsService.trackPageView(
            'single_transaction_launching_strategy_learning_center_page',
            {
              variation: 'no_strategy_rejected_transaction',
            }
          );
        },
      };
    case 'failed_payment_with_credits':
      return {
        title: t(
          'dashboard.home.it.single_payment_modal.failed_payment_with_credits.title'
        ),
        description: (
          <ModalStepDescription
            i18nKey={t(
              'dashboard.home.it.single_payment_modal.failed_payment_with_credits.description',
              {
                merchant_name: merchantName,
              }
            )}
            primaryAction={{
              children: t('dashboard.home.it.single_payment_modal.link_card'),
              onPress: () => {
                AnalyticsService.trackCustomEvent(
                  'single_transaction_launching_strategy_learning_center_link_card_button_clicked',
                  {
                    variation: 'credits_rejected_transaction',
                  }
                );
                onClose();
                router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
              },
            }}
            secondaryAction={{
              children: t('dashboard.home.it.single_payment_modal.not_now'),
              onPress: () => {
                onClose();
              },
            }}
          />
        ),
        onShow: () => {
          AnalyticsService.trackPageView(
            'single_transaction_launching_strategy_learning_center_page',
            {
              variation: 'credits_rejected_transaction',
            }
          );
        },
      };
    case 'buoni_pasto_top_up':
      return {
        title: t(
          'dashboard.home.it.single_payment_modal.buoni_pasto_top_up.title'
        ),
        description: (
          <ModalStepDescription
            i18nKey="dashboard.home.it.single_payment_modal.buoni_pasto_top_up.description"
            primaryAction={{
              children: t('dashboard.home.it.single_payment_modal.link_card'),
              onPress: () => {
                AnalyticsService.trackCustomEvent(
                  'single_transaction_launching_strategy_learning_center_link_card_button_clicked',
                  {
                    variation: 'bp_topup',
                  }
                );
                onClose();
                router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
              },
            }}
            secondaryAction={{
              children: t('dashboard.home.it.single_payment_modal.not_now'),
              onPress: () => {
                onClose();
              },
            }}
          />
        ),
        onShow: () => {
          AnalyticsService.trackPageView(
            'single_transaction_launching_strategy_learning_center_page',
            {
              variation: 'bp_topup',
            }
          );
        },
      };
    case 'with_credits':
      return {
        title: t('dashboard.home.it.single_payment_modal.with_credits.title'),
        description: (
          <ModalStepDescription
            i18nKey="dashboard.home.it.single_payment_modal.with_credits.description"
            primaryAction={{
              children: t('dashboard.home.it.single_payment_modal.link_card'),
              onPress: () => {
                AnalyticsService.trackCustomEvent(
                  'single_transaction_launching_strategy_learning_center_link_card_button_clicked',
                  {
                    variation: 'credits',
                  }
                );
                onClose();
                router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
              },
            }}
            secondaryAction={{
              children: t('dashboard.home.it.single_payment_modal.later'),
              onPress: () => {
                onClose();
              },
            }}
          />
        ),
        onShow: () => {
          AnalyticsService.trackPageView(
            'single_transaction_launching_strategy_learning_center_page',
            {
              variation: 'credits',
            }
          );
        },
      };
    case 'no_strategy':
      return {
        title: t('dashboard.home.it.single_payment_modal.no_strategy.title'),
        description: (
          <ModalStepDescription
            i18nKey="dashboard.home.it.single_payment_modal.no_strategy.description"
            primaryAction={{
              children: t('dashboard.home.it.single_payment_modal.link_card'),
              onPress: () => {
                AnalyticsService.trackCustomEvent(
                  'single_transaction_launching_strategy_learning_center_link_card_button_clicked',
                  {
                    variation: 'no_strategy',
                  }
                );
                onClose();
                router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT_AUTOMATIC);
              },
            }}
            secondaryAction={{
              children: t('dashboard.home.it.single_payment_modal.later'),
              onPress: () => {
                onClose();
              },
            }}
          />
        ),
        onShow: () => {
          AnalyticsService.trackPageView(
            'single_transaction_launching_strategy_learning_center_page',
            {
              variation: 'no_strategy',
            }
          );
        },
      };
    default:
      return null;
  }
}

function ModalStepDescription({
  i18nKey,
  primaryAction,
  secondaryAction,
}: {
  i18nKey: string;
  primaryAction?: IButtonProps;
  secondaryAction?: IButtonProps;
}) {
  return (
    <Stack gap="xl">
      <Translate i18nKey={i18nKey} parent={BodyText} />
      <Stack gap="sm">
        {primaryAction && <Button {...primaryAction} />}
        {secondaryAction && <Button {...secondaryAction} variant="tertiary" />}
      </Stack>
    </Stack>
  );
}
